import { forwardRef } from "react";
import type { ComponentPropsWithRef } from "react";
import { Search, X } from "lucide-react";
import { useFieldId } from "./useFieldId";
import { cn } from "@/lib/utils";
import { fieldControlClass } from "./field-variants";

export type SearchFieldProps = Omit<
  ComponentPropsWithRef<"input">,
  "type" | "value" | "onChange" | "placeholder"
> & {
  searchLabel: string;
  clearSearchLabel: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
  inputClassName?: string;
};

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField(
    {
      searchLabel,
      clearSearchLabel,
      id,
      name,
      value,
      onChange,
      placeholder,
      className,
      inputClassName,
      disabled = false,
      "aria-invalid": ariaInvalid,
      onKeyDown,
      ...rest
    },
    ref,
  ) {
    const inputId = useFieldId(id, name);

    return (
      <div className={cn("relative max-w-md", className)}>
        <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-surface-400">
          <Search className="h-4 w-4" aria-hidden />
        </span>
        <input
          ref={ref}
          id={inputId}
          name={name}
          type="search"
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          aria-label={searchLabel}
          aria-invalid={ariaInvalid}
          className={fieldControlClass({
            className: cn(
              "pl-10 pr-10",
              "[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden",
              inputClassName,
            ),
          })}
          {...rest}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.preventDefault();
            onKeyDown?.(event);
          }}
        />
        {value && !disabled ? (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-surface-400 hover:text-surface-600"
            aria-label={clearSearchLabel}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        ) : null}
      </div>
    );
  },
);
